import axios from 'axios'
import config from '@/config'
import { FinalPage, Flow, GlobalConfiguration, LoadingScreen } from '@/model'
import Homepage from '@/model/Homepage'
import { BenefitsTiers, OffersEligibilityTypes, ServiceTypes } from '@/enums'

const fetchClientConfiguration = async (clientId: string, locale: string): Promise<GlobalConfiguration> => {
  const date = new Date().valueOf()
  const url = config.CONTENT_BASE_URL + `/kbyg/microsite/${clientId}/global/${locale}/content.json?date=${date}`
  try {
    const { data: response } = await axios.get(url)
    return GlobalConfiguration.of(response)
  } catch {}

  const memberwebUrl = config.CONTENT_BASE_URL + `/mw/mircosite/${clientId}/global/${locale}/content.json?date=${date}`
  const retryUrl = config.CONTENT_BASE_URL + `/kbyg/microsite/member/global/${locale}/content.json?date=${date}`
  const memberwebPromise = axios.get(memberwebUrl).catch(() => { /* noop */ }) // failure of this call should be ignored
  const memberKbygPromise = axios.get(retryUrl)
  const [{ data: response }, memberwebResponse] = await Promise.all([memberKbygPromise, memberwebPromise])
  return GlobalConfiguration.of(response, memberwebResponse)
}

const fetchFlow = async (flowId: string, locale: string): Promise<Flow> => {
  const date = new Date().valueOf()
  const url = config.CONTENT_BASE_URL + `/kbyg/flows/${flowId}/${locale}/content.json?date=${date}`
  const { data: response } = await axios.get(url)
  return Flow.of(response)
}

const fetchFinalLoadingScreen = async (locale: string): Promise<LoadingScreen> => {
  const date = new Date().valueOf()
  const url = config.CONTENT_BASE_URL + `/kbyg/microsite/member/loading_screen/${locale}/content.json?date=${date}`
  const { data: response } = await axios.get(url)
  return LoadingScreen.of(response)
}

const fetchHomepage = async (clientId: string, locale: string): Promise<Homepage> => {
  const date = new Date().valueOf()

  const url = config.CONTENT_BASE_URL + `/kbyg/microsite/${clientId}/home_screen/${locale}/content.json?date=${date}`
  const retryUrl = config.CONTENT_BASE_URL + `/kbyg/microsite/member/home_screen/${locale}/content.json?date=${date}`

  const { data: response } = await axios
    .get(url)
    .catch(() => axios.get(retryUrl))

  return Homepage.of(response)
}

const fetchFinalPage = async (
  clientId: string,
  locale: string,
  serviceType: ServiceTypes,
  withOffers: OffersEligibilityTypes,
  benefitsTier: BenefitsTiers,
  packageId?: string
): Promise<FinalPage> => {
  const date = new Date().valueOf()
  const summaryType = `${serviceType}-${withOffers}-${benefitsTier}`
  const finalPageUrl = config.CONTENT_BASE_URL + `/kbyg/microsite/${clientId}/summary_screen/${summaryType}-${clientId}/${locale}/content.json?date=${date}`
  const finalPageFallbackUrl = config.CONTENT_BASE_URL + `/kbyg/microsite/member/summary_screen/${summaryType}-member/${locale}/content.json?date=${date}`

  const finalPagePromise = axios
    .get(finalPageUrl)
    .catch(() => axios.get(finalPageFallbackUrl))

  const promises = [finalPagePromise]

  if (packageId) {
    const offerUrl = config.CONTENT_BASE_URL + `/so/packages/${packageId}/${locale}/content.json?date=${date}`
    promises.push(axios.get(offerUrl))
  }

  const [finalPage, offers] = await Promise.all(promises)
  return FinalPage.of(finalPage.data, offers?.data)
}

export {
  fetchClientConfiguration,
  fetchFlow,
  fetchFinalLoadingScreen,
  fetchHomepage,
  fetchFinalPage
}
